import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Sparkles, Users, ExternalLink } from "lucide-react";
import { DIRECTORY, CANDLE_SPOTS, HEADWAY } from "../../data/site";

function Candle({ spot, item }) {
    const [hover, setHover] = useState(false);
    const inner = (
        <div
            className="group relative flex flex-col items-center"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {/* flame */}
            <div className="flame h-4 w-2.5 sm:h-5 sm:w-3" />
            {/* wax body */}
            <div className="mt-0.5 h-8 w-1.5 rounded-b-sm bg-gradient-to-b from-amber-50/90 to-amber-100/40 shadow-[0_0_10px_rgba(0,0,0,0.4)] sm:h-12" />
            {/* tooltip */}
            <div
                className={`pointer-events-none absolute top-full mt-3 w-48 -translate-x-1/2 left-1/2 rounded-xl border border-amber-500/30 bg-[#1a120c]/95 px-4 py-3 text-center shadow-xl backdrop-blur-sm transition-all duration-200 ${
                    hover
                        ? "translate-y-0 opacity-100"
                        : "translate-y-1 opacity-0"
                }`}
            >
                <p className="font-serif text-sm text-amber-100">
                    {item.title}
                </p>
                <p className="mt-0.5 text-[11px] text-amber-200/60">
                    {item.sub}
                </p>
            </div>
        </div>
    );

    const style = { top: spot.top, left: spot.left };
    return item.href ? (
        <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={style}
            data-testid={`candle-${item.n}`}
            aria-label={item.title}
        >
            {inner}
        </a>
    ) : (
        <Link
            to={item.to}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={style}
            data-testid={`candle-${item.n}`}
            aria-label={item.title}
        >
            {inner}
        </Link>
    );
}

export default function CandleHero() {
    return (
        <>
            {/* Full-bleed room photo with hanging candles */}
            <div
                data-testid="candle-hero"
                className="relative h-[82vh] min-h-[560px] w-full overflow-hidden"
            >
                <img
                    src="/images/office-hero.jpg"
                    alt="The warm counseling room where healing happens, lit by hanging candles"
                    className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-[#1a120c]" />

                {DIRECTORY.map((item, i) => (
                    <Candle key={item.n} spot={CANDLE_SPOTS[i]} item={item} />
                ))}
            </div>

            {/* Intro / headline section */}
            <section
                data-testid="intro-section"
                className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-20 pt-14 sm:pb-24 sm:pt-16"
            >
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    className="flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-amber-400/90"
                >
                    <Flame className="h-3.5 w-3.5" />
                    NeuroPlay Counseling, LLC · Longwood, Florida
                </motion.p>

                <motion.h1
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.05 }}
                    className="mt-7 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-amber-50 sm:text-5xl lg:text-6xl"
                >
                    The candles are lit.
                    <br />
                    Pull up a chair. You're safe here.
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.15 }}
                    className="mt-7 max-w-xl text-base leading-relaxed text-amber-100/75 sm:text-lg"
                >
                    Play therapy for children, EMDR and trauma care for teens and
                    adults — in the very room you see above. No stock photos
                    here; this is where healing actually happens.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.25 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <a
                        href={HEADWAY}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-book-button"
                        className="group inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-semibold tracking-wide text-[#1F1510] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-amber-400"
                    >
                        <Sparkles className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
                        Book a Visit
                    </a>
                    <Link
                        to="/about"
                        data-testid="hero-explore-button"
                        className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 px-7 py-3.5 text-sm font-semibold tracking-wide text-amber-100 transition-[background-color,border-color] duration-300 hover:border-amber-400 hover:bg-amber-500/10"
                    >
                        Meet the practice
                        <Users className="h-4 w-4" />
                    </Link>
                </motion.div>

                <p className="mt-9 hidden text-xs tracking-wide text-amber-200/50 md:block">
                    Ten candles hang from this ceiling — hover one, and see
                    where it leads. Psst: the owl and the hat have secrets too.
                </p>
            </section>
        </>
    );
}
